import { FOOTPRINT_CELL_SPAN } from "../document/footprint-cells.ts";

/**
 * Prop depth regions (ADR-098 decisions 8 to 12): parts of one frame that sort by lines of their own. A frame's
 * `LineDepth` is painted on the collision cell grid and saved per frame in the library, beside its footprint, and
 * read as saved data: a frame is drawn whole whatever it holds (ADR-132 decision 1).  Everything here is plain data in whole
 * numbers.
 */

/**
 * A rectangle of collision cells counted from a frame's bottom-left, as a footprint's cells are: the first
 * column, the lowest row, then how many columns and rows it spans.
 */
export type CellBox = readonly [column: number, rowUp: number, columns: number, rows: number];

/**
 * One line of a frame's depth: the area that sorts by it, and `up`, the line's height in whole pixels above
 * the frame's bottom edge, from 0 to the frame's height.
 */
export interface DepthLine {
  readonly up: number;
  readonly cells: readonly CellBox[];
}

/**
 * A frame's depth as it is saved. `floor` draws under every character whatever the row, as a `ground` frame
 * does. Each of `lines` sorts its area by its own base line: a wall, a rail, a door's posts. Every cell left
 * unpainted sorts by the frame's own line, where its art meets the ground, as the whole frame does without a
 * depth. `floor` present and empty is not `floor` absent: it says the frame was painted and has no floor, a
 * building with no part to stand on, which what reads the pieces may treat differently from a frame nobody
 * painted (ADR-098 note 40). A depth that names no floor and holds no rectangle is the same as none.
 */
export interface LineDepth {
  readonly floor?: readonly CellBox[];
  readonly lines?: readonly DepthLine[];
}

/**
 * The most lines one frame takes.
 */
export const MAX_DEPTH_LINES = 8;

/**
 * The most rectangles one frame's depth holds, its floor and its lines together.
 */
export const MAX_DEPTH_RECTANGLES = 48;

/**
 * The rectangles that cover exactly the cells of one kind in a grid of kinds, by one fixed rule: the
 * horizontal runs of each cell row, from the top row down and from the left, a run joined to the rectangle
 * directly above it when the two start and end on the same columns. The rectangles come out by top row, then
 * left edge.
 *
 * @param kinds The kind of each cell, row by row from the bottom row up.
 * @param columns The grid's columns.
 * @param rows The grid's rows.
 * @param kind The kind to cover.
 * @returns The rectangles.
 */
export function boxesOfKind(kinds: ArrayLike<number>, columns: number, rows: number, kind: number): CellBox[] {
  const boxes: [number, number, number, number][] = [];
  let above = new Map<number, [number, number, number, number]>();
  for (let row = rows - 1; row >= 0; row -= 1) {
    const here = new Map<number, [number, number, number, number]>();
    let column = 0;
    while (column < columns) {
      if (kinds[row * columns + column] !== kind) {
        column += 1;
        continue;
      }
      const start = column;
      while (column < columns && kinds[row * columns + column] === kind) {
        column += 1;
      }
      const open = above.get(start);
      if (open !== undefined && open[2] === column - start) {
        open[1] = row;
        open[3] += 1;
        here.set(start, open);
      } else {
        const box: [number, number, number, number] = [start, row, column - start, 1];
        boxes.push(box);
        here.set(start, box);
      }
    }
    above = here;
  }
  return boxes;
}

/**
 * Painted cells as rectangles, the canonical saved form (ADR-098 decision 11): the same cells in any order,
 * and painted any number of times, give the same rectangles in the same order.
 *
 * @param cells `[column, rowsUp]` cells from a frame's bottom-left.
 * @returns The rectangles, by top row and then left edge.
 * @throws RangeError when a coordinate is not a whole number from 0 to one under `FOOTPRINT_CELL_SPAN`.
 */
export function cellBoxes(cells: readonly (readonly [number, number])[]): CellBox[] {
  let columns = 0;
  let rows = 0;
  for (const [column, rowUp] of cells) {
    if (!Number.isInteger(column) || !Number.isInteger(rowUp) || column < 0 || rowUp < 0 || column >= FOOTPRINT_CELL_SPAN || rowUp >= FOOTPRINT_CELL_SPAN) {
      throw new RangeError(`cell [${column}, ${rowUp}] must hold whole numbers from 0 to ${FOOTPRINT_CELL_SPAN - 1}`);
    }
    columns = Math.max(columns, column + 1);
    rows = Math.max(rows, rowUp + 1);
  }
  const kinds = new Uint8Array(columns * rows);
  for (const [column, rowUp] of cells) {
    kinds[rowUp * columns + column] = 1;
  }
  return boxesOfKind(kinds, columns, rows, 1);
}

/**
 * The cells rectangles cover, in order of rows up and then column, each cell once: what a panel paints over
 * when it opens a saved depth.
 *
 * @param boxes The rectangles.
 * @returns The cells.
 */
export function boxCells(boxes: readonly CellBox[]): [number, number][] {
  const seen = new Set<number>();
  const cells: [number, number][] = [];
  for (const [column, rowUp, columns, rows] of boxes) {
    for (let r = rowUp; r < rowUp + rows; r += 1) {
      for (let c = column; c < column + columns; c += 1) {
        const key = r * FOOTPRINT_CELL_SPAN + c;
        if (!seen.has(key)) {
          seen.add(key);
          cells.push([c, r]);
        }
      }
    }
  }
  return cells.sort((a, b) => a[1] - b[1] || a[0] - b[0]);
}

/**
 * A painting as the depth that is saved: the floor's cells and each line's as rectangles (`cellBoxes`), the
 * lines in order of height, a line with no cell dropped and a kind with nothing in it left out, so equal
 * paintings are equal bytes however they were painted. `noFloor` says the frame has no floor by intent: with
 * no floor cell painted the depth then holds `floor: []`, and a painted floor wins over it.
 *
 * @param painted The floor's cells, each line's height and cells, and whether the frame is marked as having
 *   no floor.
 * @returns The depth.
 * @throws RangeError when a cell's coordinate is out of range (`cellBoxes`).
 */
export function frameDepthFromCells(painted: { readonly floor: readonly (readonly [number, number])[]; readonly lines: readonly { readonly up: number; readonly cells: readonly (readonly [number, number])[] }[]; readonly noFloor?: boolean }): LineDepth {
  const floor = cellBoxes(painted.floor);
  const lines = painted.lines
    .filter((line) => line.cells.length > 0)
    .map((line) => ({ up: line.up, cells: cellBoxes(line.cells) }))
    .sort((a, b) => a.up - b.up);
  return { ...(floor.length === 0 && painted.noFloor !== true ? {} : { floor }), ...(lines.length === 0 ? {} : { lines }) };
}

/**
 * Whether a depth says nothing: it names no floor and holds no rectangle in any line. Such a depth is the same
 * as none, and a merge that saves it removes the frame's depth. A depth whose `floor` is present, even empty,
 * says the frame was painted and is never empty.
 *
 * @param depth The depth.
 * @returns True when the depth says nothing.
 */
export function isEmptyDepth(depth: LineDepth): boolean {
  return depth.floor === undefined && (depth.lines ?? []).every((line) => line.cells.length === 0);
}

/**
 * Whether a depth cuts its frame: it holds a rectangle in its floor or in a line. One that does not, a frame
 * marked as having no floor and nothing else, draws whole and needs no pieces.
 *
 * @param depth The depth.
 * @returns True when a rectangle is painted.
 */
export function depthCuts(depth: LineDepth): boolean {
  return (depth.floor ?? []).length > 0 || (depth.lines ?? []).some((line) => line.cells.length > 0);
}

/**
 * Whether a depth marks its frame as painted with no floor: `floor` present and empty.
 *
 * @param depth The depth.
 * @returns True for the mark.
 */
export function depthHasNoFloor(depth: LineDepth): boolean {
  return depth.floor !== undefined && depth.floor.length === 0;
}
